import { useCallback, useEffect, useState } from "react";
import { LockedAutocomplete, type AutocompleteOption } from "@/components/LockedAutocomplete";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { formatCurrency } from "@/lib/documentCalc";
import {
  buildHistorischePositionenSearchParams,
  getGeschaeftspartnerQuelle,
  searchHistorischePositionen,
  type DokumentTyp,
  type HistorischeDokumentPosition,
} from "@/lib/dokumente";
import {
  listGeschaeftspartnerAnsprechpartner,
  listKontaktKunden,
  type KontaktAnsprechpartner,
} from "@/lib/kontakte";
import { listOrdner, listSOrdner, type OrdnerOut, type SOrdnerOut } from "@/lib/projects";
import { loadAppKundeOptions } from "@/pages/dokumente/kundeAutocomplete";
import { DOCUMENT_EDITOR_OVERLAY_Z } from "@/pages/dokumente/documentEditorLayers";
import {
  filterHistorischeFolderOptions,
  historischePositionenScopeHint,
  type HistorischeFolderOption,
} from "@/pages/dokumente/historischePositionenFilters";

interface KundeFilterValue {
  kundenId: number | null;
  geschaeftspartnerId: number | null;
  label: string;
}

interface AnsprechpartnerFilterValue {
  id: number;
}

interface DocumentHistorischePositionenDialogProps {
  open: boolean;
  onClose: () => void;
  dokumentTyp: DokumentTyp;
  excludeDokumentId?: number | null;
  auftragId?: number | null;
  kundenId?: number | null;
  kundeName?: string | null;
  currency?: string;
  onInsert: (item: HistorischeDokumentPosition) => void;
  zIndexClass?: string;
}

function kundeFilterOption(
  kundenId: number,
  label: string,
  geschaeftspartnerId: number | null = null,
): AutocompleteOption<KundeFilterValue> {
  const name = label.trim() || "Aktueller Kunde";
  return {
    key: `kunde-${kundenId}`,
    label: name,
    value: { kundenId, geschaeftspartnerId, label: name },
  };
}

function ansprechpartnerLabel(ap: KontaktAnsprechpartner): string {
  const name = [ap.anrede, ap.vorname, ap.nachname].filter(Boolean).join(" ").trim();
  return name || ap.mail || `Ansprechpartner #${ap.id}`;
}

function folderFromOrdner(ordner: OrdnerOut): HistorischeFolderOption {
  const description = [ordner.kunden_name, ordner.jahr ? String(ordner.jahr) : null]
    .filter(Boolean)
    .join(" · ");
  return {
    kind: "ordner",
    id: ordner.id,
    label: [ordner.nummer, ordner.beschreibung].filter(Boolean).join(" · "),
    description: description || undefined,
    kundeId: ordner.kunde_id ?? null,
  };
}

function folderFromSOrdner(ordner: SOrdnerOut): HistorischeFolderOption {
  return {
    kind: "sordner",
    id: ordner.id,
    label: [ordner.nummer, ordner.beschreibung].filter(Boolean).join(" · "),
    description: ordner.kunden_name || undefined,
    kundeId: ordner.kunde_id ?? null,
  };
}

function historischePositionHerkunft(item: HistorischeDokumentPosition): string {
  const nummer = (item.dokument_nummer || "").trim();
  if (item.quelle === "hero") {
    return nummer ? `Hero · ${nummer}` : `Hero-Dokument ${item.dokument_id}`;
  }
  return nummer || `Dok. ${item.dokument_id}`;
}

function toFolderAutocomplete(
  folder: HistorischeFolderOption,
): AutocompleteOption<HistorischeFolderOption> {
  return {
    key: `${folder.kind}-${folder.id}`,
    label: folder.label,
    description: folder.description,
    value: folder,
  };
}

export function DocumentHistorischePositionenDialog({
  open,
  onClose,
  excludeDokumentId = null,
  kundenId = null,
  kundeName = null,
  currency = "EUR",
  onInsert,
  zIndexClass = DOCUMENT_EDITOR_OVERLAY_Z,
}: DocumentHistorischePositionenDialogProps) {
  const [query, setQuery] = useState("");
  const [initialized, setInitialized] = useState(false);
  const [kunde, setKunde] = useState<AutocompleteOption<KundeFilterValue> | null>(null);
  const [ansprechpartner, setAnsprechpartner] =
    useState<AutocompleteOption<AnsprechpartnerFilterValue> | null>(null);
  const [ordner, setOrdner] = useState<AutocompleteOption<HistorischeFolderOption> | null>(null);
  const [ansprechpartnerListe, setAnsprechpartnerListe] = useState<KontaktAnsprechpartner[]>([]);
  const [apZuordnung, setApZuordnung] = useState<"offen" | "fehlt">("offen");
  const [folderCatalog, setFolderCatalog] = useState<HistorischeFolderOption[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<HistorischeDokumentPosition[]>([]);

  const filterKundenId = kunde?.value.kundenId ?? null;
  const filterGpId = kunde?.value.geschaeftspartnerId ?? null;
  const kundeOhneZuordnung = Boolean(kunde) && filterKundenId == null;
  const filterApId = ansprechpartner?.value.id ?? null;
  const filterOrdnerId = ordner?.value.kind === "ordner" ? ordner.value.id : null;
  const filterSordnerId = ordner?.value.kind === "sordner" ? ordner.value.id : null;

  useEffect(() => {
    if (!open) {
      setInitialized(false);
      setQuery("");
      setKunde(null);
      setAnsprechpartner(null);
      setOrdner(null);
      setError(null);
      setItems([]);
      return;
    }
    setQuery("");
    setAnsprechpartner(null);
    setOrdner(null);
    setError(null);
    setKunde(kundenId ? kundeFilterOption(kundenId, kundeName || "") : null);
    setApZuordnung("offen");
    setInitialized(true);
  }, [open, kundenId, kundeName]);

  useEffect(() => {
    if (!open || !filterKundenId || filterGpId) return;
    const label = (kunde?.label || "").trim();
    if (!label || label === "Aktueller Kunde") {
      setApZuordnung("fehlt");
      return;
    }
    let cancelled = false;
    setApZuordnung("offen");
    void (async () => {
      try {
        const res = await listKontaktKunden({ q: label, limit: 20 });
        const normalized = label.toLocaleLowerCase("de");
        const exact = (res.items || []).filter(
          (gp) => gp.firmenname.trim().toLocaleLowerCase("de") === normalized,
        );
        const pool = exact.length > 0 ? exact : (res.items || []).slice(0, 5);
        for (const gp of pool) {
          const quelle = await getGeschaeftspartnerQuelle(gp.id);
          if (cancelled) return;
          if (quelle.kunden_id === filterKundenId) {
            setKunde((current) => {
              if (!current || current.value.kundenId !== filterKundenId) return current;
              if (current.value.geschaeftspartnerId === gp.id) return current;
              return {
                ...current,
                value: { ...current.value, geschaeftspartnerId: gp.id },
              };
            });
            return;
          }
        }
        if (!cancelled) setApZuordnung("fehlt");
      } catch {
        if (!cancelled) setApZuordnung("fehlt");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, filterKundenId, filterGpId, kunde?.label]);

  useEffect(() => {
    if (!open || !filterGpId) {
      setAnsprechpartnerListe([]);
      return;
    }
    let cancelled = false;
    void listGeschaeftspartnerAnsprechpartner(filterGpId)
      .then((list) => {
        if (!cancelled) setAnsprechpartnerListe(list || []);
      })
      .catch(() => {
        if (!cancelled) setAnsprechpartnerListe([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, filterGpId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setFoldersLoading(true);
    void Promise.all([listOrdner().catch(() => [] as OrdnerOut[]), listSOrdner().catch(() => [] as SOrdnerOut[])])
      .then(([ordnerListe, sordnerListe]) => {
        if (cancelled) return;
        setFolderCatalog([
          ...ordnerListe.map(folderFromOrdner),
          ...sordnerListe.map(folderFromSOrdner),
        ]);
      })
      .finally(() => {
        if (!cancelled) setFoldersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !initialized) return;
    if (kundeOhneZuordnung) {
      setItems([]);
      setError("Dieser Kunde ist keinem Dokumenten-Kunden zugeordnet.");
      setLoading(false);
      return;
    }
    const handle = window.setTimeout(() => {
      void (async () => {
        setLoading(true);
        setError(null);
        try {
          const list = await searchHistorischePositionen(
            buildHistorischePositionenSearchParams({
              q: query,
              kundenId: filterKundenId,
              ansprechpartnerId: filterApId,
              ordnerId: filterOrdnerId,
              sordnerId: filterSordnerId,
              excludeDokumentId,
              limit: 100,
            }),
          );
          setItems(list);
        } catch (err) {
          setError(
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : "Suche fehlgeschlagen.",
          );
          setItems([]);
        } finally {
          setLoading(false);
        }
      })();
    }, 250);
    return () => window.clearTimeout(handle);
  }, [
    open,
    initialized,
    query,
    kundeOhneZuordnung,
    filterKundenId,
    filterApId,
    filterOrdnerId,
    filterSordnerId,
    excludeDokumentId,
  ]);

  const loadKundeOptions = useCallback(async (text: string) => {
    const options = await loadAppKundeOptions(text);
    return options.map((option) => ({
      key: option.key,
      label: option.label,
      description: option.description,
      value: {
        kundenId: null,
        geschaeftspartnerId: option.value.id,
        label: option.label,
      },
    }));
  }, []);

  const loadAnsprechpartnerOptions = useCallback(
    async (text: string) => {
      const q = text.trim().toLocaleLowerCase("de");
      return ansprechpartnerListe
        .filter((ap) => {
          if (!q) return true;
          return ansprechpartnerLabel(ap).toLocaleLowerCase("de").includes(q);
        })
        .slice(0, 30)
        .map((ap) => ({
          key: String(ap.id),
          label: ansprechpartnerLabel(ap),
          description: ap.mail || ap.telefonnummer || undefined,
          value: { id: ap.id },
        }));
    },
    [ansprechpartnerListe],
  );

  const loadOrdnerOptions = useCallback(
    async (text: string) =>
      filterHistorischeFolderOptions(folderCatalog, {
        query: text,
        kundenId: filterKundenId,
      }).map(toFolderAutocomplete),
    [folderCatalog, filterKundenId],
  );

  async function handleKundeChange(next: AutocompleteOption<KundeFilterValue> | null) {
    setAnsprechpartner(null);
    setOrdner(null);
    if (!next) {
      setKunde(null);
      setError(null);
      return;
    }
    if (next.value.kundenId) {
      setKunde(next);
      setError(null);
      return;
    }
    const gpId = next.value.geschaeftspartnerId;
    if (!gpId) {
      setError("Dieser Kunde ist keinem Dokumenten-Kunden zugeordnet.");
      return;
    }
    try {
      const quelle = await getGeschaeftspartnerQuelle(gpId);
      if (!quelle.kunden_id) {
        setError("Dieser Kunde ist keinem Dokumenten-Kunden zugeordnet.");
        return;
      }
      setError(null);
      setApZuordnung("offen");
      setKunde(kundeFilterOption(quelle.kunden_id, next.label, gpId));
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Kunde konnte nicht geladen werden.",
      );
    }
  }

  const scopeHint = historischePositionenScopeHint({
    kunde: kunde?.label,
    ansprechpartner: ansprechpartner?.label,
    ordner: ordner?.label,
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Frühere Positionen"
      description="Durchsuche Positionen aus Dokumenten der App und aus Hero und füge sie in das aktuelle Dokument ein."
      size="lg"
      zIndexClass={zIndexClass}
      footer={
        <div className="flex justify-end">
          <Button type="button" variant="secondary" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-label-secondary">Suche</span>
          <input
            type="search"
            className="eh-input w-full"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Bezeichnung, Nr., Betreff …"
            autoFocus
          />
        </label>

        <div className="grid gap-2 sm:grid-cols-3">
          <LockedAutocomplete
            label="Kunde"
            placeholder="Alle Kunden"
            value={kunde}
            onChange={(next) => {
              void handleKundeChange(next);
            }}
            loadOptions={loadKundeOptions}
            emptyText="Kein Kunde gefunden."
          />
          <LockedAutocomplete
            label="Ansprechpartner"
            placeholder="Alle Ansprechpartner"
            value={ansprechpartner}
            onChange={setAnsprechpartner}
            loadOptions={loadAnsprechpartnerOptions}
            disabled={!filterGpId}
            disabledHint={
              !kunde
                ? "Zuerst einen Kunden wählen."
                : apZuordnung === "fehlt"
                  ? "Kunde neu wählen, um Ansprechpartner zu sehen."
                  : "Ansprechpartner werden geladen …"
            }
            reloadKey={filterGpId ?? "ohne-kunde"}
            emptyText="Kein Ansprechpartner gefunden."
          />
          <LockedAutocomplete
            label="Ordner"
            placeholder="Alle Ordner"
            value={ordner}
            onChange={setOrdner}
            loadOptions={loadOrdnerOptions}
            reloadKey={`${filterKundenId ?? "alle"}-${folderCatalog.length}`}
            emptyText={foldersLoading ? "Ordner werden geladen …" : "Kein Ordner gefunden."}
          />
        </div>

        <p className="text-xs text-label-secondary">{scopeHint}</p>

        {error ? <p className="text-sm text-status-red">{error}</p> : null}

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-label-secondary">
            <Spinner />
            Suche läuft …
          </div>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm text-label-secondary">
            {query.trim() || kunde || ansprechpartner || ordner
              ? "Keine Treffer für diese Suche."
              : "Keine früheren Positionen gefunden."}
          </p>
        ) : (
          <ul className="max-h-[24rem] divide-y divide-separator overflow-y-auto rounded-lg border border-separator">
            {items.map((item) => (
              <li
                key={`${item.quelle || "app"}-${item.dokument_id}-${item.position_id}`}
                className="flex items-start gap-3 p-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-label">{item.bezeichnung}</p>
                  <p className="mt-0.5 text-xs text-label-secondary">
                    {historischePositionHerkunft(item) +
                      (item.kunde_name ? ` · ${item.kunde_name}` : "") +
                      (item.datum
                        ? ` · ${new Date(item.datum).toLocaleDateString("de-DE")}`
                        : "")}
                  </p>
                  <p className="mt-0.5 text-xs tabular-nums text-label-secondary">
                    {Number(item.menge).toLocaleString("de-DE", { maximumFractionDigits: 2 })}
                    {item.einheit ? ` ${item.einheit}` : ""} ·{" "}
                    {formatCurrency(Number(item.einzelpreis_netto) || 0, currency)}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    onInsert(item);
                    onClose();
                  }}
                >
                  Einfügen
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
